import { zodResolver } from '@hookform/resolvers/zod';
import { User } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Combobox,
  DateInput,
  FileUploader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MoneyInput,
  Textarea,
  toast,
} from '@kalos/ui';
import { Cross2Icon, PlusIcon } from '@radix-ui/react-icons';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { useAuth } from '../../../context/AuthContext';
import { useToolFundCatalogQuery } from '../../../hooks/react-query/useToolFundCatalogQuery';
import {
  useGetToolFundSettings,
  useUserToolFundBalanceAvailableQuery,
} from '../../../hooks/react-query/useToolFundSettingsQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import {
  type AdminToolPurchaseFormData,
  adminToolPurchaseSchema,
  exceedsBalance,
  makeEmptyPurchaseLine,
  purchaseTotal,
} from './adminToolPurchase';
import { findCatalogItem, requisitionPayerId, toCatalogItemOptions } from './requisitionShared';
import { useAdminToolPurchaseMutation } from './useAdminToolPurchase';
import { useIsToolFundAdmin } from './useRequisitionShared';
import { WhoPaysChoice } from './WhoPaysChoice';

export const AdminToolPurchaseForm = ({
  userId,
  onDone,
}: {
  userId: number;
  onDone: () => void;
}) => {
  const catalogQuery = useToolFundCatalogQuery({
    filter: { pageNumber: -1 },
    select: (data) => toCatalogItemOptions(data.results),
  });
  const catalogItems = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);

  const { user: me } = useAuth();
  const isToolFundAdmin = useIsToolFundAdmin();
  const canChoosePayer = isToolFundAdmin && userId !== me.id;
  const [requesteePays, setRequesteePays] = useState<boolean | undefined>();
  const payerChosen = !canChoosePayer || requesteePays !== undefined;
  const payerId = canChoosePayer
    ? requisitionPayerId({
        requesterId: me.id,
        requesteeId: userId,
        requesteePays,
        isToolFundAdmin,
      })
    : userId;
  const { data: employee } = useUserQuery({ filters: { id: userId }, enabled: canChoosePayer });
  const employeeName = employee ? `${employee.firstname} ${employee.lastname}`.trim() : 'employee';

  const balanceQuery = useUserToolFundBalanceAvailableQuery(User.create({ id: payerId }));
  const availableBalance = balanceQuery.data?.availableBalance ?? 0;
  const settingsQuery = useGetToolFundSettings({ userId: payerId });
  const payerIneligible = !!settingsQuery.data && !settingsQuery.data.isEligible;

  const form = useForm<AdminToolPurchaseFormData>({
    resolver: zodResolver(adminToolPurchaseSchema),
    defaultValues: { lines: [makeEmptyPurchaseLine()], notes: '' },
  });
  const lines = useFieldArray({ control: form.control, name: 'lines' });
  const watchedLines = useWatch({ control: form.control, name: 'lines' });
  const total = purchaseTotal(watchedLines);
  const overBalance = exceedsBalance(total, availableBalance);

  const createMutation = useAdminToolPurchaseMutation();

  const onSubmit = form.handleSubmit((data) => {
    createMutation.mutate(
      { userId, payerId, approvedBy: me.id, data },
      {
        onSuccess: ({ receiveFailures, emailFailed }) => {
          const problems = [
            receiveFailures > 0 &&
              `${receiveFailures} tool(s) couldn't be marked received. Mark them from the Tool Log.`,
            emailFailed && "The employee's approval email couldn't be sent.",
          ].filter(Boolean);
          if (problems.length > 0) {
            toast({
              variant: 'destructive',
              title: 'Tool purchase recorded',
              description: problems.join(' '),
            });
          } else {
            toast({ variant: 'success', title: 'Tool purchase recorded' });
          }
          onDone();
        },
        onError: (error) => {
          toast({
            variant: 'destructive',
            title: 'Failed to record tool purchase',
            description: error.message,
          });
        },
      },
    );
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Tools Purchased</h3>
          <p className="text-muted-foreground text-sm">
            Saving deducts the total and adds each tool to the employee&apos;s Tools tab in their
            Tool Log.
          </p>
        </div>

        {canChoosePayer && (
          <div className="space-y-2">
            <h4 className="font-semibold">Who pays</h4>
            <WhoPaysChoice
              requesteeName={employeeName}
              requesteePays={requesteePays}
              onChange={setRequesteePays}
              disabled={createMutation.isPending}
            />
            {!payerChosen && (
              <p className="text-muted-foreground text-sm">
                Choose whose tool fund pays to save the purchase.
              </p>
            )}
          </div>
        )}

        <div className="rounded-lg border">
          <div className="bg-muted/50 text-muted-foreground hidden grid-cols-[1fr_10rem_8rem_2.5rem] gap-3 border-b px-4 py-2 text-sm font-medium md:grid">
            <span>Tool *</span>
            <span>Amount *</span>
            <span>Received?</span>
            <span />
          </div>

          {lines.fields.map((line, index) => (
            <div key={line.id} className="space-y-3 border-b px-4 py-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_10rem_8rem_auto] md:items-start">
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name={`lines.${index}.catalogItemId`}
                    render={({ field }) => (
                      <FormItem>
                        <Combobox
                          values={catalogItems}
                          value={field.value ? field.value.toString() : ''}
                          onChange={(value) => {
                            const item = findCatalogItem(catalogItems, Number(value));
                            field.onChange(item?.id ?? 0);
                            form.setValue(`lines.${index}.itemName`, item?.name ?? '');
                            form.setValue(`lines.${index}.amount`, item?.cost ?? 0);
                          }}
                          isLoading={catalogQuery.isPending}
                          placeholder="Search the tool catalog..."
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {!!watchedLines[index]?.catalogItemId && (
                    <FormField
                      control={form.control}
                      name={`lines.${index}.itemName`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input {...field} placeholder="What was actually bought" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`lines.${index}.amount`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <MoneyInput
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`lines.${index}.received`}
                  render={({ field }) => (
                    <FormItem className="flex h-9 items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => field.onChange(v === true)}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Has it</FormLabel>
                    </FormItem>
                  )}
                />

                <Button
                  type="button"
                  variant="outline"
                  disabled={lines.fields.length === 1}
                  onClick={() => lines.remove(index)}
                >
                  <Cross2Icon /> Remove tool
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name={`lines.${index}.purchaseDate`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Purchase date *</FormLabel>
                      <FormControl>
                        <DateInput {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`lines.${index}.assetTag`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asset tag</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="AT-001234" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`lines.${index}.internalPurchase`}
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0 lg:mt-8">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => field.onChange(v === true)}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Bought by Kalos</FormLabel>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name={`lines.${index}.receipt`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Receipt *</FormLabel>
                      <FormControl>
                        <FileUploader
                          value={field.value ? [field.value] : []}
                          onValueChange={(files) =>
                            field.onChange(Array.isArray(files) ? files[0] ?? null : null)
                          }
                          accept={{
                            'application/pdf': ['.pdf'],
                            'image/*': ['.jpg', '.jpeg', '.png'],
                          }}
                          maxSize={5 * 1024 * 1024}
                          maxFiles={1}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`lines.${index}.photo`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Photo</FormLabel>
                      <FormControl>
                        <FileUploader
                          value={field.value ? [field.value] : []}
                          onValueChange={(files) =>
                            field.onChange(Array.isArray(files) ? files[0] ?? null : null)
                          }
                          accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] }}
                          maxSize={10 * 1024 * 1024}
                          maxFiles={1}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => lines.append(makeEmptyPurchaseLine())}
            >
              <PlusIcon /> Add tool
            </Button>
            <div className="text-right text-sm">
              <div>
                Total deduction{' '}
                <span className="ml-2 text-base font-semibold">${total.toFixed(2)}</span>
              </div>
              {payerChosen && (
                <div className={overBalance ? 'text-destructive' : 'text-muted-foreground'}>
                  {payerId === userId
                    ? 'Available balance'
                    : `${me.firstname} ${me.lastname}'s available balance`}{' '}
                  ${availableBalance.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Optional; shown as Comments on each tool" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {payerChosen && payerIneligible && (
          <p className="text-destructive text-sm">
            Whoever pays isn&apos;t eligible for the tool fund, so the purchase can&apos;t be
            charged to them.
          </p>
        )}

        {payerChosen && overBalance && (
          <p className="text-destructive text-sm">
            The total is more than the available tool fund balance of whoever pays.
          </p>
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={
              !payerChosen ||
              overBalance ||
              payerIneligible ||
              balanceQuery.isPending ||
              settingsQuery.isPending
            }
            isLoading={createMutation.isPending}
          >
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};
